import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faBalanceScale,
  faChartLine,
  faCheckCircle,
  faClock,
  faExchangeAlt,
  faHandHoldingUsd,
  faAdjust,
} from '@fortawesome/free-solid-svg-icons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import { api } from '../../config/api';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Title,
  Tooltip,
  Legend
);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const money = (value) =>
  `£${(parseFloat(value) || 0).toFixed(2)}`;

const Statistics = () => {
  const { theme } = useTheme();
  const { currentUser } = useAuth();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [categories, setCategories] = useState([]);
  const [reimbursement, setReimbursement] = useState([]);

  const isDark = theme === 'dark';
  const axisColor = isDark ? '#9aa3b2' : '#5a6270';
  const gridColor = isDark ? 'rgba(242,244,248,0.08)' : 'rgba(16,17,20,0.08)';
  const primary = isDark ? '#6b8cff' : '#2155ff';
  const sharedColor = isDark ? '#5eb4e8' : '#0f5f8a';
  const personalColor = isDark ? '#3dd68c' : '#0f7a4c';

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [summaryRes, monthlyRes, categoriesRes, reimbursementRes] = await Promise.all([
          api.get('/statistics/summary'),
          api.get('/statistics/monthly', { params: { year } }),
          api.get('/statistics/categories', {
            params: {
              startDate: `${year}-01-01`,
              endDate: `${year}-12-31`,
            },
          }),
          api.get('/statistics/reimbursement'),
        ]);

        if (summaryRes.data.success) setSummary(summaryRes.data.data);
        if (monthlyRes.data.success) setMonthly(monthlyRes.data.data || []);
        if (categoriesRes.data.success) setCategories(categoriesRes.data.data || []);
        if (reimbursementRes.data.success) setReimbursement(reimbursementRes.data.data || []);
      } catch (err) {
        console.error('Error loading statistics:', err);
        setError('Failed to load statistics. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [year]);

  const chartDefaults = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          labels: { color: axisColor },
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = context.parsed.y ?? context.parsed;
              return `${context.dataset.label ? `${context.dataset.label}: ` : ''}${money(value)}`;
            },
          },
        },
      },
      scales: {
        x: {
          ticks: { color: axisColor },
          grid: { color: gridColor },
        },
        y: {
          beginAtZero: true,
          ticks: {
            color: axisColor,
            callback: (value) => `£${value}`,
          },
          grid: { color: gridColor },
        },
      },
    }),
    [axisColor, gridColor]
  );

  const trendChart = useMemo(() => {
    const totals = MONTHS.map((_, i) => {
      const row = monthly.find((m) => Number(m.month) === i + 1);
      return row ? parseFloat(row.totalAmount || 0) : 0;
    });
    const shared = MONTHS.map((_, i) => {
      const row = monthly.find((m) => Number(m.month) === i + 1);
      return row ? parseFloat(row.sharedAmount || 0) : 0;
    });
    const personal = MONTHS.map((_, i) => {
      const row = monthly.find((m) => Number(m.month) === i + 1);
      return row ? parseFloat(row.personalAmount || 0) : 0;
    });

    return {
      labels: MONTHS,
      datasets: [
        {
          label: 'Total',
          data: totals,
          borderColor: primary,
          backgroundColor: `${primary}33`,
          fill: true,
          tension: 0.3,
        },
        {
          label: 'With reclaim',
          data: shared,
          borderColor: sharedColor,
          backgroundColor: 'transparent',
          tension: 0.3,
        },
        {
          label: 'No reclaim',
          data: personal,
          borderColor: personalColor,
          backgroundColor: 'transparent',
          borderDash: [5, 4],
          tension: 0.3,
        },
      ],
    };
  }, [monthly, primary, sharedColor, personalColor]);

  const topCategories = useMemo(
    () =>
      [...categories]
        .map((item) => ({
          id: item.Category?.id,
          name: item.Category?.name || 'Uncategorized',
          amount: parseFloat(item.totalAmount || 0),
          color: item.Category?.color || primary,
        }))
        .filter((c) => c.amount > 0)
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 6),
    [categories, primary]
  );

  const categoryChart = useMemo(
    () => ({
      labels: topCategories.map((c) => c.name),
      datasets: [
        {
          data: topCategories.map((c) => c.amount),
          backgroundColor: topCategories.map((c) => c.color),
          borderWidth: 0,
        },
      ],
    }),
    [topCategories]
  );

  const yearMix = useMemo(() => {
    const withReclaim = monthly.reduce(
      (sum, row) => sum + parseFloat(row.sharedAmount || 0),
      0
    );
    const noReclaim = monthly.reduce(
      (sum, row) => sum + parseFloat(row.personalAmount || 0),
      0
    );
    const total = withReclaim + noReclaim;
    return { withReclaim, noReclaim, total };
  }, [monthly]);

  const frontedChart = useMemo(() => {
    const rows = reimbursement.map((entry) => ({
      name: entry.user?.displayName || 'User',
      amount: parseFloat(entry.owedToUser || 0),
      isMe: entry.user?.id === currentUser?.id,
    }));

    return {
      labels: rows.map((r) => (r.isMe ? `${r.name} (you)` : r.name)),
      datasets: [
        {
          label: 'Outstanding reclaim (approx.)',
          data: rows.map((r) => r.amount),
          backgroundColor: rows.map((r) => (r.isMe ? primary : sharedColor)),
          borderRadius: 8,
        },
      ],
    };
  }, [reimbursement, currentUser, primary, sharedColor]);

  const settlement = summary?.settlement || {};
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  const ytdSpend = monthly.reduce((sum, row) => sum + parseFloat(row.totalAmount || 0), 0);
  const activeMonthCount = monthly.filter((row) => parseFloat(row.totalAmount || 0) > 0).length;
  const yearMonthlyAverage = activeMonthCount > 0 ? ytdSpend / activeMonthCount : 0;
  const peakMonth = monthly.reduce(
    (best, row) =>
      parseFloat(row.totalAmount || 0) > parseFloat(best.totalAmount || 0) ? row : best,
    { month: 1, totalAmount: 0 }
  );

  if (loading && !summary) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Loading statistics…</p>
      </div>
    );
  }

  if (error && !summary) {
    return (
      <Alert variant="danger" className="my-3">
        <Alert.Heading>Error</Alert.Heading>
        <p className="mb-0">{error}</p>
      </Alert>
    );
  }

  return (
    <div className="statistics-dashboard">
      <div className="page-header">
        <div>
          <p className="eyebrow">Insights</p>
          <h1>Statistics</h1>
          <p className="text-muted mb-0">
            Household spend, reclaim balance, and settlement progress.
          </p>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <Button as={Link} to="/analysis" variant="outline-primary" size="sm">
            Analysis
            <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
          </Button>
          <Form.Select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            style={{ width: '8rem' }}
            aria-label="Select year"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Form.Select>
        </div>
      </div>

      {error && (
        <Alert variant="warning" className="mb-3">
          {error}
        </Alert>
      )}

      <div className="balance-strip">
        <div className="summary-tile">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div className="label">You owe</div>
            <span className="summary-tile-icon">
              <FontAwesomeIcon icon={faHandHoldingUsd} />
            </span>
          </div>
          <div className="value">{money(summary?.youOwe)}</div>
          <div className="small text-muted mt-2">Remaining reclaim</div>
        </div>
        <div className="summary-tile">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div className="label">Owed to you</div>
            <span className="summary-tile-icon">
              <FontAwesomeIcon icon={faBalanceScale} />
            </span>
          </div>
          <div className="value">{money(summary?.owedToYou)}</div>
          <div className="small text-muted mt-2">
            {summary?.partner?.displayName
              ? `From ${summary.partner.displayName}`
              : 'Partner reclaim'}
          </div>
        </div>
        <div className="summary-tile">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div className="label">Net position</div>
            <span className="summary-tile-icon">
              <FontAwesomeIcon icon={faExchangeAlt} />
            </span>
          </div>
          <div className="value">
            {summary?.netBalance >= 0 ? '+' : '−'}
            {money(Math.abs(summary?.netBalance || 0))}
          </div>
          <div className="small text-muted mt-2">
            {(summary?.netBalance || 0) >= 0 ? 'In your favour' : 'You are behind'}
          </div>
        </div>
        <div className="summary-tile">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div className="label">{year} spend</div>
            <span className="summary-tile-icon">
              <FontAwesomeIcon icon={faChartLine} />
            </span>
          </div>
          <div className="value">{money(ytdSpend)}</div>
          <div className="small text-muted mt-2">
            Avg {money(yearMonthlyAverage)} / active month
          </div>
        </div>
      </div>

      <section className="panel mb-4">
        <div className="panel-head d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h2 className="h5 mb-0">Settlement health</h2>
          <Button as={Link} to="/expenses" variant="outline-primary" size="sm">
            Review outstanding
            <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
          </Button>
        </div>
        <div className="panel-body">
          <div className="settlement-grid">
            <div className="settlement-stat">
              <FontAwesomeIcon icon={faClock} className="text-warning me-2" />
              <div>
                <div className="fw-semibold">{settlement.pendingCount || 0} pending</div>
                <div className="small text-muted">No contribution yet</div>
              </div>
            </div>
            <div className="settlement-stat">
              <FontAwesomeIcon icon={faAdjust} className="text-info me-2" />
              <div>
                <div className="fw-semibold">{settlement.partialCount || 0} partial</div>
                <div className="small text-muted">
                  {money(settlement.amountContributed)} contributed so far
                </div>
              </div>
            </div>
            <div className="settlement-stat">
              <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
              <div>
                <div className="fw-semibold">{settlement.settledCount || 0} settled</div>
                <div className="small text-muted">Fully reimbursed (all time)</div>
              </div>
            </div>
            <div className="settlement-stat">
              <div>
                <div className="fw-semibold">{money(settlement.remainingReclaim)}</div>
                <div className="small text-muted">
                  Still open of {money(settlement.originalReclaimOutstanding)} reclaim
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="dashboard-grid mb-4">
        <section className="panel">
          <div className="panel-head">
            <h2 className="h5 mb-0">{year} spend trend</h2>
          </div>
          <div className="panel-body">
            <div className="chart-frame">
              <Line data={trendChart} options={chartDefaults} />
            </div>
            <p className="small text-muted mb-0 mt-3">
              Peak month: {MONTHS[(peakMonth.month || 1) - 1]} ({money(peakMonth.totalAmount)}).
              With reclaim vs no reclaim reflects each expense&apos;s split method (set at creation).
            </p>
          </div>
        </section>

        <section className="panel">
          <div className="panel-head d-flex justify-content-between align-items-center">
            <h2 className="h5 mb-0">Top categories</h2>
            <Button as={Link} to="/analysis" variant="outline-primary" size="sm">
              Drill down
            </Button>
          </div>
          <div className="panel-body">
            {topCategories.length === 0 ? (
              <p className="text-muted mb-0">No category spend for {year}.</p>
            ) : (
              <>
                <div className="chart-frame chart-frame-sm">
                  <Doughnut
                    data={categoryChart}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom',
                          labels: { color: axisColor, boxWidth: 12 },
                        },
                        tooltip: chartDefaults.plugins.tooltip,
                      },
                    }}
                  />
                </div>
                <ul className="category-leaderboard mt-3 mb-0">
                  {topCategories.map((cat) => (
                    <li key={cat.id || cat.name}>
                      {cat.id != null ? (
                        <Link to={`/analysis/categories/${cat.id}?year=${year}`}>{cat.name}</Link>
                      ) : (
                        <span>{cat.name}</span>
                      )}
                      <strong>{money(cat.amount)}</strong>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </section>
      </div>

      <div className="dashboard-grid mb-4">
        <section className="panel">
          <div className="panel-head">
            <h2 className="h5 mb-0">Who is owed</h2>
          </div>
          <div className="panel-body">
            {frontedChart.labels.length < 2 ? (
              <p className="text-muted mb-0">Need both household members for this view.</p>
            ) : (
              <>
                <div className="chart-frame chart-frame-sm">
                  <Bar
                    data={frontedChart}
                    options={{
                      ...chartDefaults,
                      plugins: {
                        ...chartDefaults.plugins,
                        legend: { display: false },
                      },
                    }}
                  />
                </div>
                <p className="small text-muted mb-0 mt-3">
                  Approximate outstanding reclaim still due to each person (after partials).
                </p>
              </>
            )}
          </div>
        </section>

        <section className="panel">
          <div className="panel-head">
            <h2 className="h5 mb-0">Mix this year</h2>
          </div>
          <div className="panel-body">
            <div className="mix-meters">
              <div>
                <div className="d-flex justify-content-between mb-1">
                  <span>With reclaim</span>
                  <strong>{money(yearMix.withReclaim)}</strong>
                </div>
                <div className="meter">
                  <span
                    style={{
                      width: `${
                        yearMix.total
                          ? Math.min(100, (100 * yearMix.withReclaim) / yearMix.total)
                          : 0
                      }%`,
                      background: sharedColor,
                    }}
                  />
                </div>
              </div>
              <div>
                <div className="d-flex justify-content-between mb-1">
                  <span>No reclaim</span>
                  <strong>{money(yearMix.noReclaim)}</strong>
                </div>
                <div className="meter">
                  <span
                    style={{
                      width: `${
                        yearMix.total
                          ? Math.min(100, (100 * yearMix.noReclaim) / yearMix.total)
                          : 0
                      }%`,
                      background: personalColor,
                    }}
                  />
                </div>
              </div>
            </div>
            <p className="small text-muted mb-0 mt-3">
              Expenses are already split on creation. With reclaim means a reimbursement is due;
              no reclaim means each person keeps what they paid.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Statistics;
